import { useCallback, useEffect, useState } from 'react'
import { Box } from '@mui/material'
import { useNavigate } from '@reach/router'
import { useMutation } from 'react-query'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import { CheckFillIcon, CloseRoundFullIcon } from 'src/components/shared/ui/Icons'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import Loader from 'src/components/shared/ui/Loader/Loader'
import { MESSAGE, ACUSES } from 'src/constants/routes'
import { theme } from 'src/providers/ThemeProvider'
import * as MessageService from 'src/services/MessageService'
import { VerificacionStyles as styles } from './VerificacionEnvioEscritoStyles'

enum Status {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR'
}

type Props = {
  idAcuseLexnet: number
  sended: boolean
}

export default function VerificacionEnvioEscrito({ idAcuseLexnet, sended }: Props): JSX.Element {
  const navigate = useNavigate()
  const [status, setStatus] = useState(Status.INITIAL)

  const downloadMessageMutation = useMutation(
    async (): Promise<any> => {
      setStatus(Status.LOADING)
      if (idAcuseLexnet > 0) {
        return MessageService.downloadMessage(idAcuseLexnet.toString())
      }
      setStatus(Status.ERROR)
      throw new Error('No se ha recibido el id del acuse')
    },
    {
      onSuccess: () => {
        setStatus(Status.SUCCESS)
      },
      onError: () => {
        setStatus(Status.ERROR)
      }
    }
  )

  useEffect(() => {
    if (sended && idAcuseLexnet > 0) {
      downloadMessageMutation.mutate()
    }
    // eslint-disable-next-line
  }, [idAcuseLexnet, sended])

  const handleClickedRow = useCallback(() => {
    navigate(MESSAGE(idAcuseLexnet.toString()))
    // eslint-disable-next-line
  }, [navigate])

  const goTolist = useCallback(() => {
    navigate(ACUSES())
  }, [navigate])

  const getMessage = useCallback(() => {
    switch (status) {
      case Status.INITIAL:
        return ''
      case Status.LOADING:
        return 'Recuperando datos del acuse de recibo'
      case Status.SUCCESS:
        return 'Acuse de recibo generado correctamente'
      case Status.ERROR:
        return '¡Error inesperado!'
      default:
        return ''
    }
  }, [status])

  const getClassName = useCallback(() => {
    switch (status) {
      case Status.INITIAL:
        return ''
      case Status.LOADING:
        return 'loading'
      case Status.SUCCESS:
        return 'success'
      case Status.ERROR:
        return 'error'
      default:
        return ''
    }
  }, [status])

  const renderIcon = useCallback((): JSX.Element | null => {
    switch (status) {
      case Status.INITIAL:
        return null
      case Status.LOADING:
        return (
          <Box sx={styles.loader}>
            <Loader size="small" />
          </Box>
        )
      case Status.SUCCESS:
        return <CheckFillIcon htmlColor={theme.colors.seaGreen100} />
      case Status.ERROR:
        return <CloseRoundFullIcon htmlColor={theme.colors.persianRed100} />
      default:
        return null
    }
  }, [status])

  const renderInfoBarMessage = useCallback((): JSX.Element => {
    switch (status) {
      case Status.INITIAL:
        return <p>Escrito enviado correctamente.</p>
      case Status.LOADING:
        return <p>Escrito enviado correctamente.</p>
      case Status.SUCCESS:
        return (
          <p>
            Escrito enviado correctamente. Puedes acceder al detalle del acuse de recibo pinchando
            <Box
              component="button"
              sx={(styles as any).linkButton}
              type="button"
              onClick={handleClickedRow}
            >
              AQUÍ
            </Box>
            .
          </p>
        )
      case Status.ERROR:
        return (
          <p>
            Escrito enviado correctamente. ¡Lo sentimos! No hemos podido generar el acuse de recibo
            del escrito enviado. Puedes acceder al listado de acuses pinchando
            <Box
              component="button"
              sx={(styles as any).linkButton}
              type="button"
              onClick={goTolist}
            >
              AQUÍ
            </Box>
            .
          </p>
        )
      default:
        return <></>
    }
  }, [status, handleClickedRow, goTolist])

  return (
    <>
      <Box sx={{ margin: '16px 0' }}>
        <InfoBar message={renderInfoBarMessage()} showCloseIcon={false} type={AlertType.SUCCESS} />
      </Box>
      <Box component="div" sx={styles.container} className={getClassName()}>
        <Box className={getClassName()} sx={styles.stageListItem}>
          <Box sx={styles.row1}>
            <Box sx={styles.statusText}>Acuse de recibo</Box>
            <Box sx={styles.statusMessage} className={getClassName()}>
              {getMessage()}
            </Box>
            <Box sx={styles.statusIcon} className={getClassName()}>
              {renderIcon()}
            </Box>
          </Box>
          <Box sx={styles.rowProgress}>
            <Box sx={styles.progress} />
          </Box>
        </Box>
      </Box>
    </>
  )
}
